import { UHttp } from '../../utils/UHttp';

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

export interface Option {
  id: string;
  name: string;
  description?: string;
  price: number;
  image?: string;
  imageVariants?: {
    original?: string;
    medium?: string;
  };
  categoryId: string;
  isActive: boolean;
  durationMinutes?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface OptionResponse<T = any> {
  success: boolean;
  message: string;
  optionId?: string;
  option?: Option;
  data?: T;
}

export interface OptionsResponse {
  success: boolean;
  message: string;
  data: Option[];
}

export interface CreateOptionData {
  name: string;
  description: string;
  price: number;
  categoryId: string;
}

export interface UpdateOptionData {
  name?: string;
  description?: string;
  price?: number;
}

export class OptionModel {
  private static instance: OptionModel;

  private constructor() {}

  public static getInstance(): OptionModel {
    if (!OptionModel.instance) {
      OptionModel.instance = new OptionModel();
    }
    return OptionModel.instance;
  }

  public async getOptions(categoryId: string): Promise<OptionsResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/option/category/${categoryId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error('Error OptionModel.getOptions:', error);
      throw error;
    }
  }

  public async getOptionById(optionId: string): Promise<OptionResponse<Option>> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/api/option/detail/${optionId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionModel.getOptionById ${optionId}:`, error);
      throw error;
    }
  }

  public async createOption(optionData: FormData): Promise<OptionResponse<Option>> {
    try {
      const response = await UHttp.postFormData(
        `${apiUrlServer}/api/option`,
        optionData,
        UHttp.authHeaders()
      );
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error('Error OptionModel.createOption:', error);
      throw error;
    }
  }

  public async updateOption(optionId: string, optionData: FormData): Promise<OptionResponse<Option>> {
    try {
      const response = await UHttp.putFormData(
        `${apiUrlServer}/api/option/update/${optionId}`,
        optionData,
        UHttp.authHeaders()
      );
      
      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionModel.updateOption ${optionId}:`, error);
      throw error;
    }
  }

  public async toggleOptionStatus(optionId: string, isActive: boolean): Promise<OptionResponse<Option>> {
    try {
      let response;
      if (isActive) {
        response = await UHttp.put(
          `${apiUrlServer}/api/option/enable/${optionId}`,
          {},
          UHttp.authHeaders()
        );
      } else {
        response = await UHttp.delete(
          `${apiUrlServer}/api/option/disable/${optionId}`,
          UHttp.authHeaders()
        );
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionModel.toggleOptionStatus ${optionId}:`, error);
      throw error;
    }
  }

  public async deleteOption(optionId: string): Promise<OptionResponse> {
    try {
      const response = await UHttp.delete(
        `${apiUrlServer}/api/option/delete/${optionId}`,
        UHttp.authHeaders()
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error: any) {
      console.error(`Error OptionModel.deleteOption ${optionId}:`, error);
      throw error;
    }
  }
}
